import { Component, computed, DestroyRef, inject, input, model, signal } from '@angular/core';
import { TagComponent } from 'shared/components/tag/tag.component';
import { DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, catchError, switchMap, tap, throttleTime, throwError } from 'rxjs';
import { CategoriesService } from 'core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-product-card',
  imports: [TagComponent, DecimalPipe, TranslatePipe],
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss'
})
export class ProductCardComponent {
  readonly #router = inject(Router);
  readonly #categoriesService = inject(CategoriesService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #favoriteClick$ = new Subject<void>();
  readonly #failedImageUrl = signal<string | null>(null);

  readonly name = input.required();
  readonly category = input.required();
  readonly price = input.required<number>();
  readonly image = input.required<string | null | undefined>();
  readonly productId = input.required<number>();
  readonly isFavorite = model<boolean>();
  readonly label = input<string>();

  readonly displayImage = computed(() => {
    const imageUrl = this.image()?.trim();

    return imageUrl && imageUrl !== this.#failedImageUrl() ? imageUrl : null;
  });

  constructor() {
    this.#favoriteClick$
      .pipe(
        throttleTime(1000),
        tap(() => this.isFavorite.set(!this.isFavorite())),
        switchMap(() =>
          this.#categoriesService.toggleFavoriteProduct(this.productId()).pipe(
            catchError(error => {
              this.isFavorite.set(!this.isFavorite());

              return throwError(() => error);
            })
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  onCardClick(): void {
    this.#router.navigate(['workspace', 'rewards', 'product', this.productId()]);
  }

  markAsFavorite(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.#favoriteClick$.next();
  }

  onImageError(): void {
    const imageUrl = this.image()?.trim();

    if (imageUrl) {
      this.#failedImageUrl.set(imageUrl);
    }
  }
}
